/**
 * Provides APIs to retrieve information about current state of containers exists in the Redux store.
 * These APIs can be used for different purposes e.g. clean up or dirty handling
 * @module ContainerUtils
 */
/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import { parseResponse } from '../router/app-router';
import Constants from '../constants';
import * as ContextUtils from '../utils/context-utils';
import * as RoutingManager from '../router/routing-manager';
import Utils from '../utils/utils';
import { getLocaleUtils } from '../locale/locale-utils';
import EnvironmentInfo from '../environment-info';
import {
  addContainerItem,
  mergeData,
  removeContainerItem,
  purgeTransientData as createPurgeTransientDataAction,
  clearTransientData as createClearTransientDataAction,
  updateTransientData as updateTransientDataAction,
  replaceTransientData as replaceTransientDataAction
} from '../store/state/actions/action-creators';
import { getStore, dispatch } from '../store/state/state-manager';
import type { Action } from '../store/state/state-manager';
import { isContextDirty, FORM_DIRTY_MESSAGE } from '../store/state/middlewares/property-change-middleware';
import { areChildContextsInDirtyState } from '../store/state/epics/util';
import * as actionType from '../store/state/actions/index';
import ActiveContext from '../store/state/active-context';

import FlowContainer from './flow-container';
import type { TransientContainerInfo, TransientContainerInfoForUpdate, NonEmptyString, ContainerInfo } from './types';

const containerLimitMap: { [key: string]: number } = {
  defaultLimit: 16
};

export const CONTAINER_TYPES = {
  SINGLE: 'single',
  MULTIPLE: 'multiple'
};

export const CONTAINER_NAMES = {
  PRIMARY: 'primary',
  WORKAREA: 'workarea',
  MODAL: 'modal',
  PREVIEW: 'preview'
};

/**
 * this function returns containers data from store
 * @function getContainers
 * @returns -- containers data
 * @private
 *
 * @example  <caption>Example for getContainers().</caption>
 * PCore.getContainerUtils().getContainers();
 * "example for getting all containers info"
 */
const getContainers = () => {
  return getStore()?.getState()?.containers;
};

function isNonEmptyString(value: string | null | undefined): value is NonEmptyString {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * this function returns the container data for the given container name
 * @function getContainerData
 * @param target -- target container
 * @returns - State Object for the given container
 * @static
 *
 * @example  <caption>Example for getContainerData.</caption>
 * PCore.getContainerUtils().getContainerData('app/primary');
 * example for getting containers info under app/primary target
 */
export const getContainerData = (target: string | null) => {
  if (!isNonEmptyString(target)) {
    return null;
  }
  return getStore()?.getState().containers[target];
};

/**
 * this function returns the container data for the given container name
 * @private
 * @function getContainerItemData
 * @param target -- target container
 * @param containerItem -- target container item
 * @returns - State Object for the given container item
 * @static
 *
 * @example  <caption>Example for getContainerItemData.</caption>
 * PCore.getContainerUtils().getContainerItemData('app/primary', 'app/primary_1');
 * example for getting container item app/primary_1 data under app/primary target
 */
export const getContainerItemData = (target: string | null | undefined, containerItem: string | null) => {
  if (!isNonEmptyString(target) || !isNonEmptyString(containerItem)) {
    return null;
  }
  const containersInfo = getContainers();
  const container = containersInfo?.[target];
  return (container && container.items && container.items[containerItem]) || null;
};

/**
 * this function returns the container items data for the given target
 * @function getContainerItems
 * @param target -- target container
 * @returns - container items data for the given target
 * @static
 *
 * @example  <caption>Example for getContainerItems.</caption>
 * PCore.getContainerUtils().getContainerItems('app/primary');
 * example for getting container items info under app/primary target
 */
export const getContainerItems = (target: string): object | null => {
  if (!isNonEmptyString(target)) {
    return null;
  }
  return getStore()?.getState().containers[target]?.items;
};

/**
 * this function returns the container accessOrder data for the given target
 * @function getContainerAccessOrder
 * @param target -- target container
 * @returns returns -- container accessOrder data for the given target
 * @static
 *
 * @example  <caption>Example for getContainerAccessOrder.</caption>
 * PCore.getContainerUtils().getContainerAccessOrder('app/primary');
 * example for getting container accessOrder info under app/primary target
 */
export const getContainerAccessOrder = (target: string): any[] => {
  if (!isNonEmptyString(target)) {
    return [];
  }
  return getStore()?.getState().containers[target]?.accessedOrder || [];
};

/**
 * this function returns the container items present or not for the given target
 * @function areContainerItemsPresent
 * @param target -- target container
 * @returns - container items present or not for the given target
 * @static
 *
 * @example  <caption>Example for areContainerItemsPresent.</caption>
 * PCore.getContainerUtils().areContainerItemsPresent('app/primary');
 * example for are container items present under app/primary target
 */
export const areContainerItemsPresent = (target: string) => {
  if (!isNonEmptyString(target)) {
    return false;
  }
  return getContainerAccessOrder(target)?.length > 0 || false;
};

/**
 * this function determines whether a containeritem present with given key in a target container.
 * @function isContainerItemExists
 * @param target -- target container  e.g. app/primary
 * @param key -- unique key name e.g app/view/1
 * @param callback A function to execute on unique key in each containerItem until the function returns true,
 * indicating that the satisfying containerItem was found. if callback is undefined, we use strict comparison for key match.
 * @returns returns -- true if containeritem already exists in store OR
 *                            -- false if no containeritem found with param information
 * @static
 *
 * @example  <caption>Example for isContainerItemExists.</caption>
 * PCore.getContainerUtils().isContainerItemExists('app/primary', 'R-1234');
 * example for getting to know whether 'R-1234' Work Item under 'app/primary' target exists or not
 */
export const isContainerItemExists = (target: string | null, key: string, callback?: Function) => {
  if (!isNonEmptyString(target) || !isNonEmptyString(key)) {
    return false;
  }
  const containersInfo = getContainers();
  const container = containersInfo?.[target];
  const containerItems = (container && container.items) || null;
  const comparatorFunction = callback || ((k: string, v: string) => k === v);
  if (key && containerItems) {
    for (const itemKey of Object.keys(containerItems)) {
      if (comparatorFunction(key, containerItems[itemKey].key)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * this function returns containeritem name if unique key is present in containers data.
 * @function getContainerItemName
 * @param target -- target container  e.g. app/primary
 * @param key -- key name e.g. app/view/1
 * @param callback -- A function to execute on unique key in each containerItem until the function returns true,
 * indicating that the satisfying containerItem was found. if callback is undefined, we use strict comparison for key match.
 * @returns - item name if container already exists in store OR
 *          - null if no container found with param information
 * @static
 *
 * @example  <caption>Example for getContainerItemName.</caption>
 * PCore.getContainerUtils().getContainerItemName('app/primary', 'R-1234');
 * example for getting container item name for 'R-1234' Work Item under 'app/primary' target.
 */
export const getContainerItemName = (target: string | null, key: string | undefined, callback?: Function) => {
  if (!isNonEmptyString(target) || !isNonEmptyString(key)) {
    return null;
  }
  const containersInfo = getContainers();
  const container = containersInfo?.[target];
  const containerItems = (container && container.items) || null;
  const comparatorFunction = callback || ((k: string, v: string) => k?.toLowerCase() === v?.toLowerCase());
  if (key && containerItems) {
    for (const itemKey of Object.keys(containerItems)) {
      if (comparatorFunction(key, containerItems[itemKey].key)) {
        return itemKey;
      }
    }
  }
  return null;
};

const findContainerByKey = (key: string, containerItems: { [key: string]: ContainerInfo }) => {
  const comparatorFunction = (k: string | undefined, v: string | undefined) => k?.toLowerCase() === v?.toLowerCase();
  if (key && containerItems) {
    for (const itemKey of Object.keys(containerItems)) {
      if (comparatorFunction(key, containerItems[itemKey].key)) {
        return containerItems[itemKey];
      }
    }
  }

  return null;
};

/**
 * @private
 * @function getContainerItemByKey
 * this function returns containeritem name if unique key is present in containers data
 * @param key - key name eg: 'R-1234'
 * @returns - item if container already exists in store OR
 *          - null if no container found with param information
 * @example  <caption>Example for getContainerItemByKey.</caption>
 * PCore.getContainerUtils().getContainerItemByKey('R-1234');
 */
export const getContainerItemByKey = (key: string | undefined) => {
  if (!isNonEmptyString(key)) {
    return null;
  }
  const containersInfo = getContainers() ?? {};
  for (const containerKey of Object.keys(containersInfo)) {
    if (containersInfo[containerKey]) {
      const container = containersInfo[containerKey];
      const containerItems = container.items ?? null;
      const containerData = findContainerByKey(key, containerItems);
      if (containerData) {
        return containerData;
      }
    }
  }
  return null;
};

/**
 * this function gives whether container is initialized in corresponding context
 * @function isContainerInitialized
 * @param context -- context of the container e.g. app/primary_1
 * @param name -- container name e.g. workarea
 * @returns - is container initialized
 * @static
 *
 * @example  <caption>Example for isContainerInitialized.</caption>
 * PCore.getContainerUtils().isContainerInitialized('app', 'primary');
 * example for getting to know whether container initialized or not for provided information
 */
export const isContainerInitialized = (context: string, name: string) => {
  // we will check if under container there exist a key with ${context}/${name}
  if (!isNonEmptyString(context) || !isNonEmptyString(name)) {
    return false;
  }
  const containersInfo = getContainers();
  return containersInfo && Object.hasOwn(containersInfo, `${context}/${name}`);
};

/**
 * this function gives the container type
 * @function getContainerType
 * @param context -- context of the container e.g. app/primary_1
 * @param name -- container name e.g. workarea
 * @returns returns -- container type e.g single or multiple
 * @static
 *
 * @example <caption>Example for getContainerType.</caption>
 * PCore.getContainerUtils().getContainerType('app', 'primary');
 * example for getting container type for provided information
 */
export const getContainerType = (context: string, name: string): null | string => {
  if (!isNonEmptyString(context) || !isNonEmptyString(name)) {
    return null;
  }
  const containersInfo = getContainers();
  const containerItem = containersInfo?.[`${context}/${name}`];
  return containerItem && containerItem.type;
};

/**
 * this functions gives active containeritem under provided target container
 * @function getActiveContainerItemName
 * @param target -- target container e.g. app/primary
 * @returns - container name e.g. app/primary_4
 * @static
 *
 * @example <caption>Example for getActiveContainerItemName.</caption>
 * PCore.getContainerUtils().getActiveContainerItemName('app/primary_1/workarea');
 * example for getting Active Container ItemName under 'app/primary_1/workarea' target.
 *
 * @example <caption>Example for getActiveContainerItemName.</caption>
 * PCore.getContainerUtils().getActiveContainerItemName('app/primary');
 * example for getting Active Container ItemName under 'app/primary' target.
 */
export const getActiveContainerItemName = (target: string | null): null | string => {
  if (!isNonEmptyString(target)) {
    return null;
  }
  let activeItemName = null;
  const containers = getContainers();
  if (containers && containers[target]) {
    const targetContainerInfo = containers[target];
    const { accessedOrder } = targetContainerInfo;
    activeItemName = accessedOrder && accessedOrder.length > 0 ? accessedOrder[accessedOrder.length - 1] : null;
  }
  return activeItemName;
};

/**
 * this functions helps to determine if containeritem is active or not with given inputs
 * @function isContainerItemActive
 * @param target -- target container e.g. app/primary
 * @param key -- unique key of the containeritem  e.g app/view/1
 * @param callback -  A function to execute on unique key in each containerItem until the function returns true,
 * indicating that the satisfying containerItem was found. if callback is undefined, we use strict comparison for key match.
 * @returns - 1) true if passed in containerItemName is active
 *          - 2) false if passed in containerItemName is not active
 * @static
 *
 * @example <caption>Example for isContainerItemActive with comparator function.</caption>
 * PCore.getContainerUtils().isContainerItemActive('app/primary', 'R-1234', (key, semanticURL) => {  });
 * example for checking 'R-1234' Work Item is active or not under 'app/primary' target.
 *
 *
 * @example <caption>Example for isContainerItemActive without comparator function.</caption>
 * PCore.getContainerUtils().isContainerItemActive('app/primary', 'W-67');
 * example for checking 'W-67' Work Item is active or not under 'app/primary' target.
 *
 */
export const isContainerItemActive = (target: string, key: string, callback: Function) => {
  if (!isNonEmptyString(target) || !isNonEmptyString(key)) {
    return false;
  }

  const containerItemName = getContainerItemName(target, key, callback);
  if (!containerItemName) return false;

  return containerItemName === getActiveContainerItemName(target);
};

/**
 * @private
 * this function returns the value of specified key from the container-item data
 * @function getValueFromContainerItem
 * @param containerItemName -- containerItem name e.g. app/primary_4
 * @param key -- key
 * @returns - 1) value of the key if passed in containerItem is present in any of the containers
 *          - 2) null if no data found
 *
 * @example <caption>Example for getValueFromContainerItem.</caption>
 * PCore.getContainerUtils().getDataContextName('app/primary_3', 'context');
 * example is for returning data context name for container item name --> 'app/primary_3'
 */
const getValueFromContainerItem = (containerItemName: string, key: string): string | null => {
  let value = null;
  const containers = getContainers();

  if (!containers) return null;

  for (const containerKey of Object.keys(containers)) {
    if (containers[containerKey].items) {
      const itemInfo = containers[containerKey].items[containerItemName];
      if (itemInfo) {
        value = itemInfo[key];
      } else {
        value = null;
      }
      if (value) {
        break;
      }
    }
  }

  return value;
};

/**
 * this functions gives data context name for provided containeritem
 * @function getDataContextName
 * @param containerItemName -- containerItem name e.g. app/primary_4
 * @returns - 1) data context if passed in containerItem is present in any of the containers
 *          - 2) null if no data found
 * @static
 *
 * @example <caption>Example for getDataContextName.</caption>
 * PCore.getContainerUtils().getDataContextName('app/primary_3');
 * example is for returning data context name for container item name --> 'app/primary_3'
 *
 * @example <caption>Example for getDataContextName.</caption>
 * PCore.getContainerUtils().getDataContextName('app/primary_2/workarea_1');
 * example is for returning data context name for container item name --> 'app/primary_2/workarea_1'
 */
export const getDataContextName = (containerItemName: string | null) => {
  if (!isNonEmptyString(containerItemName)) {
    return null;
  }
  return getValueFromContainerItem(containerItemName, 'context');
};

/**
 * this functions gives active container context under provided target container
 * @function getActiveContainerItemContext
 * @param target -- target container e.g. app/primary
 * @returns - container context e.g. app/primary_4
 * @static
 *
 * @example <caption>Example for getActiveContainerItemContext.</caption>
 * PCore.getContainerUtils().getActiveContainerItemContext('app/primary_1/workarea');
 * example for getting Active Container Item Context under 'app/primary_1/workarea' target.
 *
 * @example <caption>Example for getActiveContainerItemContext.</caption>
 * PCore.getContainerUtils().getActiveContainerItemContext('app/primary');
 * example for getting Active Container Item Context under 'app/primary' target.
 */
export const getActiveContainerItemContext = (target: string | null): string | null => {
  if (!isNonEmptyString(target)) {
    return null;
  }
  let context: string | null = null;
  const containers = getContainers();
  if (containers && containers[target]) {
    const targetContainerInfo = containers[target];
    const { accessedOrder } = targetContainerInfo;
    const activeItemName = accessedOrder && accessedOrder.length > 0 ? accessedOrder[accessedOrder.length - 1] : null;
    context = getDataContextName(activeItemName);
  }
  return context;
};

/**
 * This functions returns child containeritems for given parent containeritem
 * @function getChildContainerItems
 * @param containerItemName -- containeritem name e.g. app/primary_4
 * @returns e.g. array of child container item names['app/primary_4/workarea_1', 'app/primary_4/workarea_3']
 * @static
 *
 * @example <caption>Example for getChildContainerItems.</caption>
 * PCore.getContainerUtils().getChildContainerItems('app/primary_3');
 * example is for returning child containeritems present under 'app/primary_3' (parent containeritem name)
 *
 * @example <caption>Example for getChildContainerItems.</caption>
 * PCore.getContainerUtils().getChildContainerItems('app/secondary_2');
 * example is for returning child containeritems present under 'app/secondary_2' (parent containeritem name)
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export const getChildContainerItems = (containerItemName: string) => {
  if (!isNonEmptyString(containerItemName)) {
    return [];
  }

  const formattedContainerItemName = containerItemName.endsWith('/') ? containerItemName : `${containerItemName}/`;
  let childContainerItems: string[] = [];
  let isContainerItemNameExist = false;
  const containers = getContainers();

  if (!containers) return [];

  const getChildItems = (items: { [key: string]: any }) => {
    const childItemNames: string[] = [];
    const keys = Object.keys(items);
    keys.forEach((key) => {
      if (key.startsWith(formattedContainerItemName) && key !== formattedContainerItemName) {
        childItemNames.push(key);
      }

      if (key === containerItemName) {
        isContainerItemNameExist = true;
      }
    });
    return childItemNames;
  };

  for (const containerKey of Object.keys(containers)) {
    if (containers[containerKey].items) {
      const itemNames = getChildItems(containers[containerKey].items);

      if (itemNames && itemNames.length > 0) childContainerItems = childContainerItems.concat(itemNames);
      childContainerItems = childContainerItems.sort();
    }
  }

  return isContainerItemNameExist ? childContainerItems : [];
};

/**
 * This functions returns child containers for given parent container name
 * @function getChildContainers
 * @param containerItemName -- containeritem name e.g. app/primary_1
 * @returns e.g. array of child container names['app/primary_1/workarea']
 * @private
 *
 * @example <caption>Example for getChildContainers.</caption>
 * PCore.getContainerUtils().getChildContainers('app/primary_1');
 * example is for returning child containers present under 'app/primary_1' (parent containeritem name)e
 *
 * @example <caption>Example for getChildContainers.</caption>
 * PCore.getContainerUtils().getChildContainers('app/primary_2');
 * example is for returning child containers present under 'app/primary_2' (parent containeritem name)
 */
export const getChildContainers = (containerItemName: string): string[] => {
  if (!isNonEmptyString(containerItemName)) {
    return [];
  }
  const childContainers: string[] = [];
  const containers = getContainers();

  if (!containers) return [];

  for (const containerKey of Object.keys(containers)) {
    if (containerKey.startsWith(containerItemName) && containerKey !== containerItemName) {
      childContainers.push(containerKey);
    }
  }
  return childContainers;
};

/**
 * This functions returns child containers for given parent container name
 * @function getChildContainersContexts
 * @param parentContainerItemID -- containeritem name e.g. app/primary_1
 * @returns e.g. array of child container names['app/primary_1/workarea']
 * @private
 *
 * @example <caption>Example for getChildContainersContexts.</caption>
 * PCore.getContainerUtils().getChildContainers('app/primary_1');
 * example is for returning child containers present under 'app/primary_1' (parent containeritem name)e
 *
 * @example <caption>Example for getChildContainersContexts.</caption>
 * PCore.getContainerUtils().getChildContainers('app/primary_2');
 * example is for returning child containers present under 'app/primary_2' (parent containeritem name)
 */
export const getChildContainersContexts = (parentContainerItemID: string) => {
  if (!isNonEmptyString(parentContainerItemID)) {
    return [];
  }
  const childContainersContexts: (string | null)[] = [];
  const containers = getContainers();

  if (!containers) return [];

  for (const container of Object.keys(containers)) {
    if (container.startsWith(parentContainerItemID) && container !== parentContainerItemID) {
      const containerItems = getContainerAccessOrder(container) || [];
      containerItems.forEach((containerItem) => {
        const context = getDataContextName(containerItem);
        if (context !== parentContainerItemID) {
          childContainersContexts.push(getDataContextName(containerItem));
        }
      });
    }
  }
  return childContainersContexts;
};

/**
 * This functions returns suggestion contexts related to containerItemID
 * @function getSuggestionContexts
 * @param containerItemID parent containerItemID  e.g. app/primary_1
 * @returns array of related suggestion context name e.g. ['app/primary_1/suggestion,app/primary_1/workarea_1/suggestion']
 * @private
 *
 * @example <caption>Example for getSuggestionContexts.</caption>
 * PCore.getContainerUtils().getSuggestionContexts('app/primary_1');
 * example is for returning related suggestion context present for 'app/primary_1' (parent containeritemId)
 */
export const getSuggestionContexts = (containerItemID: string) => {
  const relatedContainerItemId = [...getChildContainersContexts(containerItemID), containerItemID];
  const suggestionContexts: string[] = [];
  const contexts = getStore()?.getState().data || {};
  relatedContainerItemId.forEach((itemId) => {
    if (Object.hasOwn(contexts, `${itemId}/${Constants.SUGGESTION}`)) {
      suggestionContexts.push(`${itemId}/${Constants.SUGGESTION}`);
    }
  });
  return suggestionContexts;
};

/**
 * This function determines whether container is single mode or not
 * @function isContainerSingleMode
 * @param target -- target container  e.g. app/primary
 * @returns - 1) true if passed container type is single
 *          - 2) false if passed container type is other
 * @private
 *
 * @example <caption>Example for isContainerSingleMode.</caption>
 * PCore.getContainerUtils().isContainerSingleMode('app/primary');
 * example for getting to know whether container is single mode
 *
 * @example <caption>Example for isContainerSingleMode.</caption>
 * PCore.getContainerUtils().isContainerSingleMode('app/primary_1/workarea');
 * example for getting to know whether container is single mode
 */

export const isContainerSingleMode = (target: string | null): boolean | null => {
  if (!isNonEmptyString(target)) {
    return null;
  }
  return target && getContainerData(target) && getContainerData(target).type === CONTAINER_TYPES.SINGLE;
};

/**
 * This function determines whether container is multiple mode or not
 * @function isContainerMultipleMode
 * @param target -- target container  e.g. app/primary
 * @returns - 1) true if passed container type is multiple
 *          - 2) false if passed container type is other
 * @private
 *
 * @example <caption>Example for isContainerMultipleMode.</caption>
 * PCore.getContainerUtils().isContainerMultipleMode('app/primary');
 * example for getting to know whether container is multiple mode
 *
 * @example <caption>Example for isContainerMultipleMode.</caption>
 * PCore.getContainerUtils().isContainerMultipleMode('app/primary_1/workarea');
 * example for getting to know whether container is multiple mode
 */

export const isContainerMultipleMode = (target: string | null): boolean | null => {
  if (!isNonEmptyString(target)) {
    return null;
  }
  return target && getContainerData(target) && getContainerData(target).type === CONTAINER_TYPES.MULTIPLE;
};

/**
 * This function determines whether container have items or not
 * @function hasContainerItems
 * @param target -- target container  e.g. app/primary
 * @returns - 1) true if passed container has items
 *          - 2) false if passed in container has no items
 * @private
 *
 * @example <caption>Example for hasContainerItems.</caption>
 * PCore.getContainerUtils().hasContainerItems('app/primary');
 * example for getting to know whether container has items for provided information
 *
 * @example <caption>Example for hasContainerItems.</caption>
 * PCore.getContainerUtils().hasContainerItems('app/primary_1/workarea');
 * example for getting to know whether container has items for provided information
 */
export const hasContainerItems = (target: string | null): boolean => {
  if (!isNonEmptyString(target)) {
    return false;
  }
  return (
    (getContainerData(target) &&
      getContainerData(target).items &&
      Object.keys(getContainerData(target)?.items).length > 0) ||
    false
  );
};

/**
 * This function provides container limit information.
 * @function getContainerLimit
 * @param target -- target container  e.g. app/primary
 * @returns - container limit value
 * @private
 *
 * @example <caption>Example for getContainerLimit.</caption>
 * PCore.getContainerUtils().getContainerLimit('app/primary');
 * example to know container limit for `app/primary`
 */
export const getContainerLimit = (target: string): number => {
  if (target && containerLimitMap[target]) {
    return containerLimitMap[target];
  }
  return containerLimitMap.defaultLimit;
};

/**
 * This function helps to set container limit for target.
 * @function setContainerLimit
 * @param target -- target container  e.g. app/primary
 * @param limitNumber -- limit value e.g. 8, 16, 32 etc..
 *                             -- if not limitNumber passed, default limit value 16 will be set.
 * @private
 *
 * @example <caption>Example for setContainerLimit.</caption>
 * PCore.getContainerUtils().setContainerLimit('app/primary', 8);
 * example to set container limit for `app/primary` as 8
 */
export const setContainerLimit = (target: string, limitNumber = containerLimitMap.defaultLimit) => {
  if (!isNonEmptyString(target)) {
    return;
  }
  containerLimitMap[target] = limitNumber;
};

/**
 * This function helps to know whether container limit has been reached or not.
 * @function isContainerLimitReached
 * @param target container  e.g. app/primary
 * @returns - 1) true - if container limit reaches
 *          - 2) false - if container limit not reached.
 * @private
 *
 * @example <caption>Example for isContainerLimitReached.</caption>
 * PCore.getContainerUtils().isContainerLimitReached('app/primary');
 * example to know container limit for `app/primary`
 */
export const isContainerLimitReached = (target: string | null) => {
  if (!isNonEmptyString(target)) {
    return false;
  }
  let reached = false;
  const containerData = getContainerData(target);
  const { items = {} } = containerData;
  const numberOfItems = Object.keys(items);
  const containerLimit = getContainerLimit(target);
  if (numberOfItems && numberOfItems.length > 0 && numberOfItems.length >= containerLimit) {
    reached = true;
  }
  return reached;
};

/**
 * This function helps to give payload for resetContainers.
 * @function resetContainersPayload
 * @param containerInfo -- target container  e.g. app/primary
 * @returns - contains context, Array of container items, container name
 *
 * @private
 *
 * @example <caption>Example for resetContainersPayload.</caption>
 * PCore.getContainerUtils().resetContainersPayload({ "context": "app", "name": "preview"});
 */
export const resetContainersPayload = (containerInfo: { [key: string]: any }) => {
  if (!containerInfo || !isNonEmptyString(containerInfo.name) || !isNonEmptyString(containerInfo.context)) {
    return null;
  }
  const defaultAcName = containerInfo?.name;
  const containerName = `${containerInfo?.context}/${defaultAcName}`;
  const containerData = getContainerData(containerName);
  const containerItems =
    (Array.isArray(containerData?.accessedOrder) &&
      containerData?.accessedOrder.map((containerItem: any) => {
        return containerData?.items[`${containerItem}`]?.context;
      })) ||
    [];
  const childContainers = getChildContainers(`${Constants.APP.APP}/${Constants.PREVIEW}`);
  const workAreaContexts = getChildContainersContexts(`${Constants.APP.APP}/${Constants.PREVIEW}`);
  return {
    context: containerInfo?.context,
    name: defaultAcName,
    containerItems: [...containerItems, ...workAreaContexts],
    childContainers
  };
};

/**
 * This function checks whether container content is changed or not
 * i.e. state is dirty or not
 * @function isContainerDirty
 * @param containerItemID -- container id  e.g. app/primary_1
 * @returns - returns true if container is dirty else false
 *
 * @example <caption>Example for isContainerDirty.</caption>
 * PCore.getContainerUtils().isContainerDirty("app/primary_1");
 */
export const isContainerDirty = (containerItemID: string): boolean => {
  if (!isNonEmptyString(containerItemID)) {
    return false;
  }
  const dataContextName = getDataContextName(containerItemID);
  return isContextDirty(dataContextName) || areChildContextsInDirtyState(containerItemID);
};

/**
 * This function helps to know whether container limit has been reached or not.
 * @function getLeastRecentlyUsedNonDirtyContainerItemID
 * @param target -- target container  e.g. app/primary
 * @returns - 1) containerItemID (string type) - if LRU + ND container item found.
 *          - 2) undefined - if no container item found with non dirty status.
 * @private
 *
 * @example <caption>Example for getLeastRecentlyUsedNonDirtyContainerItemID.</caption>
 * PCore.getContainerUtils().getLeastRecentlyUsedNonDirtyContainerItemID('app/primary');
 * example to know least used not dirty container item inside `app/primary`
 */
export const getLeastRecentlyUsedNonDirtyContainerItemID = (target: string | null): string | null => {
  if (!isNonEmptyString(target)) {
    return null;
  }
  let containerItemID;
  const containerData = getContainerData(target);
  const { accessedOrder = [] } = containerData;
  for (let index = 0; index < accessedOrder.length; index += 1) {
    const id = accessedOrder[index];
    if (!isContainerDirty(id)) {
      containerItemID = id;
      break;
    }
  }
  return containerItemID;
};

/**
 * This function helps to get the target of the given containerItemID.
 * @function getTargetFromContainerItemID
 * @param containerItemID -- target container  e.g. app/primary
 * @returns - return the target of the containerItem
 * @private
 *
 * @example <caption>Example for getTargetFromContainerItemID.</caption>
 * PCore.getContainerUtils().getTargetFromContainerItemID('app/primary/workarea_1');
 * example to get the target ('app/primary/workarea')  of the containerItemID 'app/primary/workarea_1'
 */
export const getTargetFromContainerItemID = (containerItemID: string | null): string | null => {
  if (!isNonEmptyString(containerItemID)) {
    return null;
  }
  const { containers } = getStore()?.getState() || {};
  if (containers) {
    for (const key of Object.keys(containers)) {
      if (containers[key]?.accessedOrder?.includes(containerItemID)) {
        return key;
      }
    }
  }

  const endIndex = containerItemID.lastIndexOf(Constants.UNDERSCORE_STRING);
  return containerItemID.slice(0, endIndex);
};

/**
 * This function used to close the container item
 * @function closeContainerItem
 * @param containerItemID -- container item which need to be closed
 * @param options options contains additional info like skipDirtyCheck to
 * @returns this returns promise
 * skip confirm dialog if container item is dirty
 * @example <caption>Example for closeContainerItem.</caption>
 * In the below example container item with id - "app/primary_4" will be closed
 * PCore.getContainerUtils().closeContainerItem("app/primary_4");
 * // pass eventName in options to identify the source event
 * PCore.getContainerUtils().closeContainerItem("app/primary_4",{skipDirtyCheck:true, eventName:'Dismiss case'});
 *
 * // pass skipDirtyCheck: true in options to prevent confirm dialog
 * PCore.getContainerUtils().closeContainerItem("app/primary_4",{skipDirtyCheck:true});
 *
 */
export const closeContainerItem = (
  containerItemID: string,
  options: { skipDirtyCheck?: boolean; eventName?: string } = {}
): Promise<object> => {
  if (!isNonEmptyString(containerItemID)) {
    return Promise.reject(new Error('closeContainerItem: containerItemID is the required parameter'));
  }
  const target = getTargetFromContainerItemID(containerItemID);
  const context = getDataContextName(containerItemID);
  const { skipDirtyCheck } = options;
  let removeItem = true;
  let newAccessedOrder = [];
  if (isContainerDirty(containerItemID) && !skipDirtyCheck) {
    // eslint-disable-next-line no-alert
    removeItem = window.confirm(getLocaleUtils().getLocaleValue(FORM_DIRTY_MESSAGE, 'Messages'));
  }
  if (removeItem) {
    const { accessedOrder = [], items = {}, type } = getContainerData(target);
    newAccessedOrder = accessedOrder.filter((item: string) => item !== containerItemID);
    const lastItemKey = newAccessedOrder[newAccessedOrder.length - 1];
    const item = items[lastItemKey] || {};
    const { semanticURL = Constants.FORWARD_SLASH_STRING, context: itemContext } = item;

    if (newAccessedOrder.length > 0 && semanticURL && semanticURL !== Constants.FORWARD_SLASH_STRING) {
      parseResponse({
        directUrlUpdation: true,
        semanticURL,
        options: {
          containerItemID: lastItemKey,
          context: itemContext
        }
      });
    } else {
      parseResponse({
        directUrlUpdation: true,
        semanticURL: Constants.FORWARD_SLASH_STRING,
        options: {}
      });
    }
    const containerInfo = { newAccessedOrder, containerType: type };
    dispatch(
      removeContainerItem({
        containerItemID,
        context,
        target,
        containerInfo,
        ...(options.eventName !== undefined ? { eventName: options.eventName } : {})
      })
    );
  }
  return Promise.resolve({
    newAccessedOrder,
    removeItem
  });
};

/**
 * Adds container item in root context
 * @example <caption>Example for addRootContainerItem</caption>
 * PCore.getContainerUtils().addRootContainerItem(root, containerName, appData, isPortal)
 * @param viewConfig - view metadata of the root view in UI resources
 * @param containerName - name of the container item
 * @param appData - app data in case of portal scenario
 * @param isPortal - boolean value portal or non-portal
 * @private
 */
export const addRootContainerItem = (viewConfig: object, containerName: string, appData: object, isPortal: boolean) => {
  const defaultBaseContext = 'root';
  const defaultContext = ContextUtils.buildContextName(containerName, defaultBaseContext, true);
  const defaultTarget = RoutingManager.getResolvedTargetName(containerName, defaultBaseContext);

  const defaultPayload = {
    root: viewConfig,
    containerItemID: defaultContext,
    context: !isPortal ? defaultContext : 'app',
    target: defaultTarget,
    containerName
  };

  const dataPayload = {
    data: appData,
    context: !isPortal ? defaultContext : 'app'
  };

  const actions = [addContainerItem(defaultPayload)];
  actions.push(mergeData(dataPayload));

  dispatch(actions);
};

const isContextBeingUsedInContainer = (target: string, currentContainerItemID: string, context: string | null) => {
  let accessedOrder = getContainerAccessOrder(target);
  accessedOrder = accessedOrder.filter((ID) => ID !== currentContainerItemID);
  let isBeingUsed = false;
  for (let index = 0; index < accessedOrder.length; index += 1) {
    const containerDataContext = getDataContextName(accessedOrder[index]);
    if (containerDataContext === context) {
      isBeingUsed = true;
      break;
    }
  }
  return isBeingUsed;
};

/**
 * this function helps to identify whether context refering by current containerItemID is being used by another container in redux store
 * @private
 * @param containerItemID - qualified container Item ID e.g. app/primary_1
 * @returns - returns true if container item context is being used else false
 */
export const isContainerItemContextBeingUsedInOtherContainers = (containerItemID: string) => {
  if (!isNonEmptyString(containerItemID)) {
    return false;
  }
  const containers = getContainers();
  const context = getDataContextName(containerItemID);
  const targets = containers ? Object.keys(containers) : [];
  let isBeingUsed = false;

  for (let index = 0; index < targets.length; index += 1) {
    isBeingUsed = isContextBeingUsedInContainer(targets[index], containerItemID, context);
    if (isBeingUsed) {
      break;
    }
  }
  return isBeingUsed;
};

/**
 * This function returns the transient items present under
 * the container such as app/primary_1/workarea
 * @private
 * @param containerItemsID target container  e.g. [app/primary_1/workarea_1]
 * @returns array of transient items for target container
 */
export const getTransientItems = (containerItemsID: string | string[]) => {
  if (!Array.isArray(containerItemsID)) {
    containerItemsID = [containerItemsID];
  }
  const containers = getContainers() || {};
  return containerItemsID
    .map((containerItemID) => {
      const target = getTargetFromContainerItemID(containerItemID);
      return Utils.getIn(containers, [target, 'items', containerItemID, 'transientItems']) || [];
    })
    .flat();
};

/**
 * Updates the values of the specified fields within a transient item.
 * @function updateTransientData
 * @param transientObject - JSON object containing information about transient item and the fields to be Updated.
 * @example updateTransientData()
 * Below api will update the transient data of item with transientItemID 'uniqueIdentifier' with data object passed without
 * resetting the other fields as reset is false in option.
 * ```
 * const containerUtils = PCore.getContainerUtils();
 * containerUtils.updateTransientData({
 *  transientItemID: 'uniqueIdentifier',
 *  data: {
 *        "Prop1": "valueA",
 *        "Prop2": "valueB"
 *  },
 *  options: {
 *    "reset": false
 *  }
 * });
 * ```
 */
export const updateTransientData = (transientObject: TransientContainerInfoForUpdate) => {
  const { transientItemID, data, options } = transientObject;
  const existingData = Utils.deepClone(getStore()?.getState().data[transientItemID]);

  if (options?.reset) {
    Utils.iterateLeafNodes(existingData, (path) => {
      if (path[path.length - 1] !== 'classID') {
        Utils.setIn(existingData, path, '');
      }
    });
  }
  dispatch(
    updateTransientDataAction({
      transientItemID,
      data: Utils.mergeDeep(data, existingData)
    })
  );
};

/**
 * Replaces the values of the specified fields and deletes the unspecified fields within a transient item.
 * @function replaceTransientData
 * @param transientObject - JSON object containing information about transient item and the fields to be replaced.
 * @example replaceTransientData()
 * Below api will replace the values of Prop1 and Prop2 and deletes Prop3 in the searchCriteria transient item.
 * ```
 * const containerUtils = PCore.getContainerUtils();
 * containerUtils.replaceTransientData({
 *  transientItemID: 'searchCriteria',
 *  data: {
 *        "Prop1": "valueA",
 *        "Prop2": "valueB"
 *  }
 * });
 * ```
 * Before api call
 * {
 *  "Prop1": "Test1",
 *  "Prop2": "Test2",
 *  "Prop3": "Test3"
 * }
 * After api call :
 * {
 *  "Prop1": "valueA",
 *  "Prop2": "valueB"
 * }
 */
export const replaceTransientData = (transientObject: TransientContainerInfo) => {
  const { transientItemID, data } = transientObject;

  dispatch(
    replaceTransientDataAction({
      transientItemID,
      data
    })
  );
};

/**
 * Deletes the data stored in a transient item.
 * @function clearTransientData
 * @param transientItemID The ID of the transient item containing the data to be deleted.
 * @example <caption>Example for clearTransientData</caption>
 * // consider transient item value is
 * // {Name: 'James Bond', department: {id: '007', type: 'agent'}};
 * PCore.getContainerUtils().clearTransientData('app/primary_1/workarea_1/transientItem_1');
 * // after calling clearTransientData, transient item data will be
 * // {Name: '', department: {id: '', type: ''}}
 */
export const clearTransientData = (transientItemID: string) => {
  const data = Utils.deepClone(getStore()?.getState().data[transientItemID]);
  Utils.iterateLeafNodes(data, (path) => {
    if (path[path.length - 1] !== 'classID') {
      Utils.setIn(data, path, '');
    }
  });
  dispatch(
    createClearTransientDataAction({
      transientItemID,
      data
    })
  );
};

/**
 * Deletes the keys and data stored in a transient item.
 * @function purgeTransientData
 * @param transientItemID The ID of the transient item containing the keys and data to be deleted.
 * @example <caption>Example for purgeTransientData</caption>
 * // consider transient item value is
 * // {Name: 'James Bond', department: {id: '007', type: 'agent'}};
 * PCore.getContainerUtils().purgeTransientData('app/primary_1/workarea_1/transientItem_1');
 * // after calling purgeTransientData, transient item data will be
 * // empty object {}
 */
export const purgeTransientData = (transientItemID: string) => {
  const data = Utils.deepClone(getStore()?.getState().data[transientItemID]);
  Utils.iterateLeafNodes(data, (path) => Utils.setIn(data, path, ''));
  dispatch(
    createPurgeTransientDataAction({
      transientItemID,
      data
    })
  );
};

/**
 *
 * Provides APIs to set, get, and container names
 * This class is independent of the context
 */

class ContainerItems {
  static items: string[];

  /**
   *  adds container items array
   * @private
   * @param items - items
   */
  static addContainerItems(items: string[]) {
    this.items = items;
  }

  /**
   *  returns container-items array
   * @private
   */
  static getContainerItems() {
    return this.items;
  }

  /**
   *  deletes container item
   * @private
   * @param containerName - container name to be deleted
   */
  static deleteContainerItem(containerName: string) {
    const index = this?.items?.indexOf(containerName);
    if (index !== undefined && index !== -1) {
      this.items.splice(index, 1);
    }
  }
}

/**
 * exposes Class which contains apis to set, get, and container names
 * @private
 * @returns - ContainerItems instance
 */
export const getContainerAPI = () => {
  return ContainerItems;
};

/**
 * Remove container from store
 * @private
 * @param containerName - name of the container
 */
export const removeContainer = (containerName: string) => {
  const payload: Action['payload'] = {
    containerName
  };
  payload.childContainers = getChildContainers(containerName);
  dispatch({
    type: actionType.REMOVE_CONTAINER,
    payload
  });
};

/**
 * This function updates the etag in child containers
 * @function updateRelatedContextEtag
 * @private
 * @param containerItemName -- containeritem name e.g. app/primary_4
 * @param etag -- latest etag
 * @example <caption>Example for updateRelatedContextEtag.</caption>
 * PCore.getContainerUtils().updateRelatedContextEtag("app/primary_4","'20211129T130156.399 GMT'")
 * after calling this etag is updated in the child containers (app/primary_4/workarea_1)
 */
export const updateRelatedContextEtag = (containerItemName: string, etag: string) => {
  const childContainerItems = getChildContainerItems(containerItemName);
  const { key } = getContainerItemData(getTargetFromContainerItemID(containerItemName), containerItemName);
  const actions: Action[] = [];

  childContainerItems.forEach((item) => {
    const { key: childContainerKey } = getContainerItemData(getTargetFromContainerItemID(item), item);
    if (childContainerKey === key) {
      const payload = {
        data: {
          caseInfo: {
            headers: {
              etag
            }
          }
        },
        target: getTargetFromContainerItemID(item),
        context: item
      };
      actions.push({
        type: actionType.MERGE_DATA,
        payload
      });
    }
  });
  if (actions.length) {
    dispatch(actions);
  }
};

/**
 * This function updates the etag in case context
 * @function updateCaseContextEtag
 * @private
 * @param containerItemName -- containeritem name e.g. app/primary_4
 * @param etag -- latest etag
 * @example <caption>Example for updateCaseContextEtag.</caption>
 * PCore.getContainerUtils().updateCaseContextEtag("app/primary_4","'20211129T130156.399 GMT'")
 * after calling this etag is updated in the case context (app/primary_4)
 */
export const updateCaseContextEtag = (containerName: string, etag: string) => {
  const actions: {
    type: string;
    payload: { data: { caseInfo: { headers: { etag: string } } }; target: string | null; context: string };
  }[] = [];
  const payload = {
    data: {
      caseInfo: {
        headers: {
          etag
        }
      }
    },
    target: getTargetFromContainerItemID(containerName),
    context: containerName
  };
  actions.push({
    type: actionType.MERGE_DATA,
    payload
  });

  if (actions.length) {
    dispatch(actions);
  }
};
/**
 * This function returns the root container name
 * @private
 * @returns - Containername -(1) if portal is loaded,app
 * --------------------------(2) If portal is not loaded, root
 */
export const getRootContainerName = () => {
  return EnvironmentInfo.isPortalLoaded ? 'app' : 'root';
};

/**
 * Obtains an entry point to the {@link ActiveContext|ActiveContext} object that contains APIs that act on the active context for the currently opened container.
 *
 * PCore.getContainerUtils().getActiveContext();
 *
 * @returns - ActiveContext instance
 * @function
 * @public
 */
export const getActiveContext = () => {
  return ActiveContext;
};

/**
 * This function updates the lastUpdateTime in primary container
 * @function updateParentLastUpdateTime
 * @private
 * @param context -- context of the container e.g. app/primary_4
 * @param lastUpdateTime -- latest lastUpdateTime
 * @example <caption>Example for updateParentLastUpdateTime.</caption>
 * PCore.getContainerUtils().updateParentLastUpdateTime("app/primary_4","2022-08-10T07:03:20.065Z")
 * after calling this lastUpdateTime is updated in the primary container (app/primary_4)
 */
export const updateParentLastUpdateTime = (context: string, lastUpdateTime: string) => {
  const payload = {
    data: {
      caseInfo: {
        lastUpdateTime
      }
    },
    target: getTargetFromContainerItemID(context),
    context
  };
  dispatch({
    type: actionType.MERGE_DATA,
    payload
  });
};

/**
 * This functions returns context for given target container and key
 * @function getContextByTargetandKey
 * @param target -- target container name e.g. app/primary
 * @param key -- key within container item to search for e.g. OO6GFG-TH1COS-WORK T-157004
 * @private
 *
 * @example <caption>Example for getContextByTargetandKey.</caption>
 * PCore.getContainerUtils().getContextByTargetandKey('app/primary', OO6GFG-TH1COS-WORK T-157004);
 * example is for returning context present under 'app/primary_2/workarea => item => app/primary_2/workarea_1 => context'
 */
export const getContextByTargetAndKey = (target: string, key: string) => {
  const containerItemID = getContainerItemName(target, key);
  const workareaContainerItemId = getContainerItemName(`${containerItemID}/workarea`, key);
  return getDataContextName(workareaContainerItemId);
};

/**
 * This function returns the parentContext value for given container item if it exists
 * @private
 * @function getContainerParentContext
 * @param containerItemName -- containeritem name e.g. app/primary_4
 * @returns - 1) parent context if exists in container item
 *          - 2) null if no data found
 *
 * @example <caption>Example for getContainerParentContext.</caption>
 * PCore.getContainerUtils().getContainerParentContext('app/primary_3/modal_1');
 * example is for returning data context name for container item name --> 'app/primary_3'
 *  */
export const getContainerParentContext = (containerItemName: string): string | null => {
  if (!isNonEmptyString(containerItemName)) {
    return null;
  }
  return getValueFromContainerItem(containerItemName, 'parentContext');
};

export const getActiveContainerItemIDFromRoutingInfo = (routingInfo: any) => {
  const { accessedOrder } = routingInfo;

  if (accessedOrder && accessedOrder.length > 0) {
    return accessedOrder[accessedOrder.length - 1];
  }
  return '';
};

/**
 * This function returns the parentContainerItem given the containerItemID.
 * @private
 * @param containerItemId
 * @returns string
 * @example <caption>Example for getContainerParentContext.</caption>
 * PCore.getContainerUtils().getParentContainerItem('app/primary_3/modal_1');
 * example is for returning data context name for container item name --> 'app/primary_3'
 */
export const getParentContainerItem = (containerItemId: string | null): string | null => {
  if (containerItemId && typeof containerItemId === 'string') {
    const splitArr = containerItemId.split('/');
    splitArr.pop();
    return splitArr.join('/');
  }
  return null;
};

export const getFlowContainer = () => FlowContainer;
